import React, { useContext, useEffect } from 'react';
import { translateBlock, translateBlockValueWithLabel } from 'i18n';
import { camelize } from 'helpers/syntaxHelper';
import TreeTablePicker from 'components/inputs/TreeTablePicker/TreeTablePicker';
import Selector from 'components/inputs/Selector';
import {
    deserializeTreeValue,
    deserializeListValue,
    serializeTreeValue,
    serializeListValue,
    optionsToFlatTree,
} from 'components/inputs/TreeTablePicker/helpers';
import { FilterOperations } from 'business/definitions';
import { useTranslation } from 'react-i18next';
import { improveLabelForDefaultScopes } from 'components/SentenceFilter/FilterManagees/helpers';
import { capitalize } from 'helpers/helpers';
import { getOperationOptions, useGetFilterValues } from './hooks';
import { GESTIONNAIRE_TABLE } from 'routes/BudgetModule/config/constants';
import { BudgetModuleContext } from 'routes/BudgetModule/components/BudgetWithProvider/BudgetModuleContext';

type Props = {
    filter: SentenceFilterWithScopesT;
    setFilter: FlowAnyFunction;
    isPerimeterFilter: boolean | null | undefined;
    isDisabled: boolean | null | undefined;
    showUnderline?: boolean;
    preProcessingUrlCallback?: (url: string) => string;
    shouldFilterValuesByScope?: boolean;
};

const TreePicker = ({
    filter,
    setFilter,
    isDisabled,
    isPerimeterFilter,
    showUnderline,
    preProcessingUrlCallback,
    shouldFilterValuesByScope = false,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    const operationOptions = getOperationOptions('tree');
    const isOperationNull = (
        [FilterOperations.is_null, FilterOperations.is_not_null] as (
            | FilterOperationsT
            | DynamicFilterOperationsT
        )[]
    ).includes(filter.operation);

    const {
        data: filterWithValues,
        isLoading,
        isError,
        error,
    } = useGetFilterValues(
        filter,
        isDisabled,
        preProcessingUrlCallback,
        shouldFilterValuesByScope,
    );

    const isUnauthorized = isError && error.status === 403;

    // In case of public dashboard, we do not fetch get_filter_values
    // and we display filter.values or filter.treeValues by default
    // TODO: to remove when we get rid of get_filter_operation_in
    let filterValues = filter.values;
    let filterTreeValues = filter.treeValues;
    if (!isLoading && !isDisabled && !isError) {
        // TODO StrictNullCheck : Try to remove "!"
        filterValues = filterWithValues!.values;
        filterTreeValues = filterWithValues!.treeValues;
    }

    useEffect(() => {
        const operationValues = operationOptions.map((o) => o.value);
        if (!operationValues.includes(filter.operation))
            setFilter({ operation: FilterOperations.in_ });
    }, [filter.operation]);

    const scopeOptions = filter.scopes ?? [];
    const scopeOptionsWithUserFriendlyLabels = improveLabelForDefaultScopes(
        scopeOptions,
        filter,
        t,
    );

    const isInBudgetModule = useContext(BudgetModuleContext);
    const shouldDisplayLabel =
        isInBudgetModule && filter.field === GESTIONNAIRE_TABLE;

    const isTree = filterTreeValues !== null && filterTreeValues !== undefined;
    const deserializedValues = isTree
        ? deserializeTreeValue(
              filter.value,
              // TODO StrictNullCheck : Try to remove "!"
              filterTreeValues!,
              (node) => node.value,
              scopeOptionsWithUserFriendlyLabels,
          )
        : deserializeListValue(
              filter.value,
              filterValues,
              scopeOptionsWithUserFriendlyLabels,
              shouldDisplayLabel,
          );
    // we want bad values to appear first, and not be buried inside a '+N filters' indication
    deserializedValues.sort((selectedVal) => (selectedVal.isBadValue ? -1 : 1));
    const serialize = isTree ? serializeTreeValue : serializeListValue;
    const gridOptions = isTree
        ? filterTreeValues
        : optionsToFlatTree(
              filterValues != null
                  ? filterValues.map(translateBlockValueWithLabel)
                  : [],
          );
    const title = translateBlock(camelize(filter.field));
    return (
        <>
            {!isPerimeterFilter && (
                <Selector
                    className="e2e-filter-operation no-flex-shrink filter-operation"
                    options={operationOptions}
                    value={filter.operation}
                    onChange={(operation) => setFilter({ operation })}
                    fullWidth={false}
                    preventSorting
                />
            )}
            {!isOperationNull && (
                <TreeTablePicker
                    isLoading={isLoading}
                    data-testid={`e2e-filter-${camelize(filter.field)}`}
                    selectedValues={deserializedValues}
                    disabled={!!isDisabled}
                    isValueLocked={isUnauthorized}
                    onChange={(value) => setFilter({ value: serialize(value) })}
                    errorText={filter.error || ''}
                    title={title}
                    placeholder={
                        isPerimeterFilter ? capitalize(title) : undefined
                    }
                    // TODO StrictNullCheck : Try to remove "!"
                    gridOptions={gridOptions!}
                    scopeOptions={scopeOptions}
                    inputProps={{
                        disableUnderline: !showUnderline,
                    }}
                />
            )}
        </>
    );
};

export const TreePickerWithUnderline = (props: Props): React.ReactElement => (
    <TreePicker {...props} showUnderline />
);
export const TreePickerWithoutUnderline = (
    props: Props,
): React.ReactElement => <TreePicker {...props} />;
